/* eslint-disable react-hooks/exhaustive-deps */
import { Box, IconButton, Link, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { DateTime } from 'luxon';
import { getBoundingBoxImages } from '../../../../store/devices/actionCreators';
import CustomerCareDTable from '../../../Shared/CustomerCareDTable';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { parseDateToLocal } from '../../../../shared/utils';
import {
  BBImageSetMetadata,
  Device,
} from '../../../../store/devices/deviceTypes';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import { isNull } from 'lodash';
import NoTabDataCard from './components/NoTabDataCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareFromSquare } from '@fortawesome/pro-light-svg-icons';
import NewModal from '../../../Shared/NewModal';
import ImageCarousel from '../../../Operations/components/ImageCarousel';
import { dimGrey, lightPurple } from '../../../../mainTheme';

interface TabComponentProps {
  selectedDevice: Device | null;
}

const SeamDetectionImagesTab: React.FC<TabComponentProps> = ({
  selectedDevice,
}) => {
  const dispatch = useDispatch();
  const { bbImages } = useSelector((state: RootState) => state.devices);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[] | null>(null);
  const [filteredImages, setFilteredImages] = useState<BBImageSetMetadata[]>(
    [],
  );
  const [selectedSentryEventId, setSelectedSentryEventId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!bbImages?.length) {
      dispatch(getBoundingBoxImages(DateTime.now().minus({ days: 7 }))); // by default
    }
  }, [dispatch]);

  useEffect(() => {
    if (bbImages && selectedDevice) {
      const filtered = bbImages.filter(
        (image) =>
          image.owner_id === selectedDevice.customer_id ||
          image.serial_number === selectedDevice.serial_number,
      );
      setFilteredImages(filtered);
    }
  }, [bbImages, selectedDevice]);

  const handleViewClick = (imageUrl: string[], sentryEventId: string) => {
    setSelectedImages(imageUrl);
    setSelectedSentryEventId(sentryEventId);
    setOpenDialog(true);
  };

  const sentryDeepLinkPrefix =
    'https://omcare.sentry.io/performance/summary/events/?project=5877591&query=id%3A';
  const sentryDeepLinkSuffix =
    '&sort=timestamp&statsPeriod=30d&transaction=Dispense';

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Image ID',
      flex: 2,
      renderCell: (params) => (
        <Link
          href={`${sentryDeepLinkPrefix}${params.row.sentry_event_id}${sentryDeepLinkSuffix}`}
          target="_blank"
          rel="noopener"
          sx={{ color: lightPurple }}
        >
          {params.row.sentry_event_id}
        </Link>
      ),
    },
    {
      field: 'prescribed_date',
      headerName: 'Prescribed date',
      flex: 1,
      valueGetter: (params) => {
        if (params.row.med_event_id === 'demo_mode') {
          return 'Demo mode';
        }
        return params.row.med_event_id ? params.row.med_event_id : 'N/A';
      },
    },
    {
      field: 'uploaded_at',
      headerName: 'Uploaded time',
      flex: 1,
      valueGetter: (params) => {
        if (!params.row.uploaded_at) return 'N/A';
        const uploadedAt = DateTime.fromISO(params.row.uploaded_at, {
          zone: 'utc',
        });
        const now = DateTime.now().setZone('utc');

        // Calculate the difference
        const diff = now.diff(uploadedAt, ['hours', 'minutes']);
        const totalHours = Math.floor(diff.hours || 0);
        const minutes = Math.floor(diff.minutes || 0);

        // Show relative time only if less than 24 hours
        if (totalHours < 24) {
          if (totalHours > 0) {
            return `${totalHours} hours ago`;
          } else if (minutes > 0) {
            return `${minutes} minutes ago`;
          }
        }

        // For all other cases, use parseDateToLocal
        return parseDateToLocal(params.row.uploaded_at, 'yyyy-MM-dd HH:mm:ss');
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams<BBImageSetMetadata>) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            border: '1px solid var(--Outline-O5---Separator, #D4D4D9)',
            borderRadius: '8px',
            padding: { xs: '2px', sm: '4px' },
            height: { xs: '32px', sm: '36px' },
          }}
        >
          <Tooltip title="View Images">
            <IconButton
              onClick={() =>
                handleViewClick(
                  params.row.images.map((img) => img.presignedUrl),
                  params.row.sentry_event_id,
                )
              }
            >
              <FontAwesomeIcon
                icon={faShareFromSquare}
                style={{
                  fontSize: '16px',
                  color: dimGrey,
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isNull(bbImages)) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        height: '100%',
      }}
    >
      {filteredImages?.length ? (
        <CustomerCareDTable
          rows={filteredImages || []}
          columns={columns}
          pageSize={10}
          defaultSortField="uploaded_at"
          defaultSortDirection="desc"
          hideViewAllButton
          loading={!bbImages}
          idField="sentry_event_id"
        />
      ) : (
        <NoTabDataCard />
      )}

      {openDialog && selectedImages && selectedSentryEventId && (
        <NewModal
          showCloseX
          title="Sentry event"
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
        >
          <Box sx={{ mb: 1 }}>
            <Link
              href={`${sentryDeepLinkPrefix}${selectedSentryEventId}${sentryDeepLinkSuffix}`}
              target="_blank"
              rel="noopener"
              sx={{ color: lightPurple }}
            >
              {selectedSentryEventId}
            </Link>
          </Box>
          <ImageCarousel images={selectedImages} />
        </NewModal>
      )}
    </Box>
  );
};

export default SeamDetectionImagesTab;
